import { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useToastAction } from '../hooks/useToastAction';
import { useOpenRequest } from '../hooks/useOpenRequest';
import { isAxiosError } from 'axios';
import {
  Table,
  type Column,
  type ContextMenuItem,
  Button,
  ContactOverlay,
  TodoModal,
  type TodoFormData,
  TableSkeleton,
  TableLoadingOverlay,
  ConfirmDialog,
  VenueModal,
  type VenueFormData,
  SearchInput,
  Pagination,
} from '../components/Common';
import { todosAPI, contactsAPI, venuesAPI, bandsAPI } from '../api/endpoints';
import { useAuth } from '../contexts/AuthContext';
import { useTodoCountsOrZero } from '../hooks/useTodos';
import { useTodoSearch } from '../hooks/useTodoSearch';
import type { Reminder, Contact, Venue, Band, TodoType, TodoDueHorizon } from '../types';
import { recencyLabel } from '../utils/recencyColor';
import { format, isPast, isToday, addDays, endOfDay } from 'date-fns';
import { X, MessageSquare, Clock, Bell, Plus, Check, Phone, ListTodo, Undo2, Pencil, Rows3, Megaphone } from 'lucide-react';
import clsx from 'clsx';

interface TodoItem {
  id: string;
  todoType: Reminder['type'];
  status: Reminder['status'];
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  venueName?: string;
  bandNames?: string[];
  description: string;
  datetime: string;
  contactId?: string;
  venueId?: string;
  // A campaign's follow-up todo: one per mailing-list send, opened on that list's page
  // instead of in the contact overlay (docs/CAMPAIGNS.md)
  mailingListId?: string;
  mailingListName?: string;
  campaignId?: string;
  reminder: Reminder;
}

interface ConfirmAction {
  type: 'snooze' | 'dismiss';
  id: string;
}

const SNOOZE_PRESETS = [
  { label: 'Tomorrow', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
] as const;

const TYPE_ICONS: Record<TodoType, typeof Bell> = { remind: Bell, contact: Phone, task: ListTodo };
const TYPE_LABELS: Record<TodoType, string> = { remind: 'Reminder', contact: 'Contact', task: 'Task' };
const TYPE_ORDER: TodoType[] = ['remind', 'contact', 'task'];

const STATUS_LABELS: Record<Reminder['status'], string> = {
  pending: 'Pending',
  done: 'Done',
  dismissed: 'Dismissed',
};
const STATUS_ORDER: Reminder['status'][] = ['pending', 'done', 'dismissed'];

const DUE_LABELS: Record<TodoDueHorizon, string> = {
  overdue: 'Overdue',
  today: 'Today',
  later: 'Later',
};
const DUE_ORDER: TodoDueHorizon[] = ['overdue', 'today', 'later'];

export function TodoPage() {
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [selectedVenueContacts, setSelectedVenueContacts] = useState<Contact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string | undefined>(undefined);
  const [isContactOverlayOpen, setIsContactOverlayOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [customSnoozeDate, setCustomSnoozeDate] = useState('');
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoItem | null>(null);
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());
  // Opening *and* refreshing the overlay go through the same venue + contacts fetch; this
  // retires one whose answer is no longer wanted (see useOpenRequest)
  const openRequest = useOpenRequest();
  const [triggeringTodoId, setTriggeringTodoId] = useState<string | null>(null);
  const [venueError, setVenueError] = useState<string | null>(null);
  const [overlayIndex, setOverlayIndex] = useState<number | null>(null);
  const [selectedTodoId, setSelectedTodoId] = useState<string | null>(null);
  const [isCompactMode, setIsCompactMode] = useState(() => localStorage.getItem('todo-compact-mode') === 'true');
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const animatingTimeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const { run } = useToastAction();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Server-paginated, URL-synced: search term, filters, sort and page all live in the query
  // string, so a view is linkable and a reload lands back on it.
  const {
    todos,
    pagination,
    isLoading,
    isFetching,
    error: todosError,
    params,
    statuses,
    types,
    due,
    isFiltered,
    setSearch,
    setStatuses,
    setTypes,
    setDue,
    setSort,
    setPage,
    resetFilters,
  } = useTodoSearch();

  // One unfiltered aggregation behind the header summary, the filter dropdown counts and the
  // navbar badge — see useTodoCounts.
  const counts = useTodoCountsOrZero();

  // Refresh every ['todos', ...] query, so both the table and the counts (and with them the
  // navbar badge) follow todos marked done, dismissed or snoozed from this page.
  const refreshTodos = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['todos'] }),
    [queryClient],
  );

  // Fetch bands for the todo modal
  const { data: bands = [] } = useQuery<Band[]>({
    queryKey: ['bands'],
    queryFn: async () => {
      const response = await bandsAPI.list();
      return response.data.items;
    },
  });

  const todoItems: TodoItem[] = useMemo(
    () =>
      todos.map((todo) => ({
        id: todo._id,
        todoType: todo.type,
        status: todo.status,
        contactName: todo.contact_name,
        contactEmail: todo.contact_email,
        contactPhone: todo.contact_phone,
        venueName: todo.venue_name,
        bandNames: todo.band_names,
        description: todo.description,
        datetime: todo.datetime,
        contactId: todo.contact_id,
        venueId: todo.venue_id,
        mailingListId: todo.mailing_list_id,
        mailingListName: todo.mailing_list_name,
        campaignId: todo.campaign_id,
        reminder: todo,
      })),
    [todos],
  );

  // The overlay's prev/next arrows walk the page on screen, so navigation never has to wait on
  // a fetch or re-paginate the table under the user.
  const navigableItems = useMemo(() => todoItems.filter((item) => item.venueId != null), [todoItems]);

  const handleMarkDone = useCallback((id: string) => {
    setAnimatingIds((prev) => new Set(prev).add(id));
    const existing = animatingTimeouts.current.get(id);
    if (existing) clearTimeout(existing);

    const toastId = toast.loading('Marking as done...');
    const timeout = setTimeout(() => {
      todosAPI.done(id).then(
        () => {
          toast.success('Marked as done', { id: toastId });
          refreshTodos();
        },
        (err: unknown) => {
          const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
          toast.error(detail || 'Failed to mark as done', { id: toastId, duration: 5000 });
        },
      ).finally(() => {
        setAnimatingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        animatingTimeouts.current.delete(id);
      });
    }, 400);
    animatingTimeouts.current.set(id, timeout);
  }, [refreshTodos]);

  const handleUndone = useCallback((id: string) => {
    run(
      () => todosAPI.update(id, { status: 'pending' }).then(() => refreshTodos()),
      { loading: 'Marking as pending...', success: 'Moved back to pending' },
    );
  }, [refreshTodos, run]);

  const handleDismiss = (id: string) => {
    setConfirmAction(null);
    run(
      () => todosAPI.dismiss(id).then(() => refreshTodos()),
      { loading: 'Dismissing...', success: 'Todo dismissed' },
    );
  };

  const handleSnooze = (id: string, days: number) => {
    setConfirmAction(null);
    run(
      () => todosAPI.snooze(id, days).then(() => refreshTodos()),
      { loading: 'Snoozing...', success: 'Todo snoozed' },
    );
  };

  const handleSnoozePreset = (days: number) => {
    if (!confirmAction || confirmAction.type !== 'snooze') return;
    handleSnooze(confirmAction.id, days);
  };

  const handleSnoozeCustomDate = () => {
    if (!confirmAction || confirmAction.type !== 'snooze' || !customSnoozeDate) return;
    const target = new Date(customSnoozeDate + 'T12:00:00');
    const now = new Date();
    const diffDays = Math.max(1, Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    handleSnooze(confirmAction.id, diffDays);
  };

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'dismiss') {
      handleDismiss(confirmAction.id);
    }
  };

  const handleCreateTodo = (data: TodoFormData): void => {
    run(
      () => todosAPI.create({
        description: data.description,
        datetime: data.datetime,
        type: data.type,
        contact_id: data.contact_id,
        band_ids: data.band_ids,
      }).then(() => refreshTodos()),
      { loading: 'Creating todo...', success: 'Todo created' },
    );
  };

  const handleEditTodo = (data: TodoFormData): void => {
    if (!editingTodo) return;
    const id = editingTodo.id;
    setEditingTodo(null);
    run(
      () => todosAPI.update(id, {
        description: data.description,
        datetime: data.datetime,
        type: data.type,
        contact_id: data.contact_id,
        band_ids: data.band_ids,
      }).then(() => refreshTodos()),
      { loading: 'Saving todo...', success: 'Todo saved' },
    );
  };

  /** Open the overlay on a venue, or refresh the one already open — the overlay's
   *  `onRefreshContacts` / `onRefreshVenues` come back through here.
   *
   *  Which is why the token matters: the refresh is a venue fetch plus one request per contact,
   *  and a contact carries its mail bodies, so it runs for seconds. The overlay asks for a
   *  refresh at the end of actions that *close* it — deleting the last contact of the venue does
   *  both — and without the guard the fetch landing afterwards ran `setIsContactOverlayOpen(true)`
   *  and put the overlay back on screen by itself, its navigation state already cleared by the
   *  close, so the prev/next arrows were dead on top of it. */
  const handleOpenVenueContacts = async (venueId?: string, contactId?: string) => {
    if (!venueId) return;
    const token = openRequest.begin();

    try {
      const venueResponse = await venuesAPI.getById(venueId);
      const venue = venueResponse.data;
      if (!openRequest.isCurrent(token)) return;
      setSelectedVenue(venue);

      // Fetch contacts for this venue
      const contactIds = venue.contact_ids || [];
      const contactPromises = contactIds.map((id) => contactsAPI.getById(id));
      const contactResponses = await Promise.all(contactPromises);
      if (!openRequest.isCurrent(token)) return;
      setSelectedVenueContacts(contactResponses.map((r) => r.data));
      setSelectedContactId(contactId);
      setIsContactOverlayOpen(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        setVenueError('This venue has been deleted. You should dismiss this todo.');
      } else if (isAxiosError(error) && error.response?.status) {
        setVenueError(`Failed to load venue (error ${error.response.status})`);
      } else {
        setVenueError('Failed to load venue');
      }
    }
  };

  const handleEditVenue = (venue: Venue) => {
    setEditingVenue(venue);
    setIsVenueModalOpen(true);
  };

  const handleCloseVenueModal = () => {
    setIsVenueModalOpen(false);
    setEditingVenue(null);
  };

  const handleVenueSubmit = (venueData: VenueFormData): void => {
    if (!editingVenue) return;
    const id = editingVenue._id;
    run(
      () => venuesAPI.update(id, venueData).then((res) => {
        // Keep the open contact overlay in sync with the edited venue
        setSelectedVenue(prev => (prev && prev._id === id ? res.data : prev));
        refreshTodos();
        return res;
      }),
      { loading: 'Updating venue...', success: 'Venue updated' },
    );
  };

  const handleDeleteVenue = (id: string): void => {
    run(
      () => venuesAPI.delete(id).then((res) => {
        openRequest.cancel();
        setIsContactOverlayOpen(false);
        setSelectedVenue(null);
        setSelectedVenueContacts([]);
        refreshTodos();
        return res;
      }),
      { loading: 'Deleting venue...', success: 'Venue deleted' },
    );
  };

  const handleToggleCompactMode = () => {
    const next = !isCompactMode;
    setIsCompactMode(next);
    localStorage.setItem('todo-compact-mode', String(next));
  };

  /** A campaign's follow-up todo opens the mailing list it went to, on that campaign. */
  const handleOpenMailingList = useCallback((item: TodoItem) => {
    if (!item.mailingListId) return;
    const params = new URLSearchParams({ list: item.mailingListId });
    if (item.campaignId) params.set('campaign', item.campaignId);
    navigate(`/mailing-lists?${params.toString()}`);
  }, [navigate]);

  const handleOpenTodo = (item: TodoItem) => {
    if (item.mailingListId) {
      handleOpenMailingList(item);
      return;
    }
    if (!item.venueId) return;
    const idx = navigableItems.findIndex(i => i.id === item.id);
    if (idx !== -1) {
      setOverlayIndex(idx);
    }
    setSelectedTodoId(item.id);
    if (item.status === 'pending' && (item.todoType === 'remind' || item.todoType === 'contact')) {
      setTriggeringTodoId(item.id);
    }
    handleOpenVenueContacts(item.venueId, item.contactId);
  };

  const handleOverlayNavigate = (direction: 'prev' | 'next') => {
    if (overlayIndex === null) return;
    const target = overlayIndex + (direction === 'next' ? 1 : -1);
    if (target < 0 || target >= navigableItems.length) return;
    const item = navigableItems[target];
    setOverlayIndex(target);
    setSelectedTodoId(item.id);
    handleOpenVenueContacts(item.venueId!, item.contactId);
  };

  const overlayHasPrev = overlayIndex !== null && overlayIndex > 0;
  const overlayHasNext = overlayIndex !== null && overlayIndex < navigableItems.length - 1;

  const getRowContextMenu = (row: TodoItem): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];

    if (row.status === 'pending') {
      items.push({
        label: 'Edit',
        icon: <Pencil className="w-4 h-4" />,
        onClick: () => setEditingTodo(row),
      });
      items.push({
        label: 'Mark as done',
        icon: <Check className="w-4 h-4" />,
        onClick: () => handleMarkDone(row.id),
      });
    } else {
      items.push({
        label: 'Mark as pending',
        icon: <Undo2 className="w-4 h-4" />,
        onClick: () => handleUndone(row.id),
      });
    }

    if (row.venueId) {
      items.push({
        label: 'Contact Venue',
        icon: <MessageSquare className="w-4 h-4" />,
        onClick: () => handleOpenTodo(row),
      });
    }

    if (row.mailingListId) {
      items.push({
        label: 'Open mailing list',
        icon: <Megaphone className="w-4 h-4" />,
        onClick: () => handleOpenMailingList(row),
      });
    }

    if (row.status === 'pending') {
      items.push({
        label: 'Snooze...',
        icon: <Clock className="w-4 h-4" />,
        onClick: () => {
          setCustomSnoozeDate('');
          setConfirmAction({ type: 'snooze', id: row.id });
        },
      });
    }

    if (row.status !== 'dismissed') {
      items.push({
        label: 'Dismiss',
        icon: <X className="w-4 h-4" />,
        onClick: () => setConfirmAction({ type: 'dismiss', id: row.id }),
        danger: true,
      });
    }

    return items;
  };

  const typeFilterValues = useMemo(
    () => TYPE_ORDER.map((t) => ({ value: t, label: TYPE_LABELS[t], count: counts.by_type[t] ?? 0 })),
    [counts.by_type],
  );

  // The due counts are pending-only (an overdue *done* todo is not a thing), so they match
  // the header line rather than the all-status totals the other two dropdowns show.
  const dueFilterValues = useMemo(
    () => DUE_ORDER.map((h) => ({ value: h, label: DUE_LABELS[h], count: counts[h] ?? 0 })),
    [counts],
  );

  const statusFilterValues = useMemo(
    () => STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABELS[s], count: counts.by_status[s] ?? 0 })),
    [counts.by_status],
  );

  const columns: Column<TodoItem>[] = [
    {
      key: 'description',
      label: 'Description',
      render: (row) => {
        const Icon = TYPE_ICONS[row.todoType] || Bell;
        return (
          <div className={clsx('flex items-center gap-2', row.status !== 'pending' && 'opacity-60')}>
            <Icon className={clsx('w-4 h-4 shrink-0', row.status === 'done' ? 'text-green-500' : 'text-dark-text-muted')} />
            <span className={clsx('font-medium', row.status !== 'pending' && 'line-through')}>{row.description}</span>
          </div>
        );
      },
    },
    {
      key: 'venueName',
      label: 'Venue',
      width: '180px',
      render: (row) => (
        <div className={clsx(row.status !== 'pending' && 'opacity-60')}>
          {row.venueName ? (
            <span className="font-medium">{row.venueName}</span>
          ) : row.mailingListId ? (
            // A campaign's follow-up stands for a whole list, not a venue: the list's name is
            // the row's only name, and it is the link to the send (docs/CAMPAIGNS.md).
            <button
              onClick={(e) => { e.stopPropagation(); handleOpenMailingList(row); }}
              className="flex items-center gap-1.5 text-left text-primary-400 hover:text-primary-300 underline underline-offset-2 decoration-dotted"
              title="Open this mailing list's campaign"
            >
              <Megaphone className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium truncate">{row.mailingListName ?? 'Mailing list'}</span>
            </button>
          ) : (
            <span className="text-dark-text-muted">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'bandNames',
      label: 'Bands',
      width: '150px',
      render: (row) => (
        <div className={clsx(row.status !== 'pending' && 'opacity-60')}>
          {row.bandNames && row.bandNames.length > 0 ? (
            <span className="font-medium">{row.bandNames.join(', ')}</span>
          ) : (
            <span className="text-dark-text-muted">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'contactName',
      label: 'Contact',
      width: '200px',
      render: (row) => {
        const displayName = row.contactName || row.contactEmail || row.contactPhone;
        if (!displayName) {
          return <span className="text-dark-text-muted">-</span>;
        }

        const secondaryInfo = row.contactName
          ? row.contactEmail || row.contactPhone
          : row.contactEmail && row.contactPhone
            ? row.contactPhone
            : undefined;

        return (
          <div className={clsx(row.status !== 'pending' && 'opacity-60')}>
            <div className="font-medium">{displayName}</div>
            {secondaryInfo && (
              <div className="text-xs text-dark-text-muted">{secondaryInfo}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      filterable: true,
      filterConfig: {
        values: typeFilterValues,
        selectedValues: types,
        onChange: (selected) => setTypes(selected as TodoType[]),
      },
      width: '120px',
      render: (row) => {
        const Icon = TYPE_ICONS[row.todoType] || Bell;
        return (
          <div className={clsx('flex items-center gap-1.5 text-sm', row.status !== 'pending' && 'opacity-60')}>
            <Icon className="w-3.5 h-3.5 shrink-0 text-dark-text-muted" />
            <span>{TYPE_LABELS[row.todoType] ?? row.todoType}</span>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterConfig: {
        values: statusFilterValues,
        selectedValues: statuses,
        onChange: (selected) => setStatuses(selected as Reminder['status'][]),
      },
      width: '120px',
      render: (row) => (
        <span
          className={clsx(
            'text-sm',
            row.status === 'done' && 'text-green-400',
            row.status === 'dismissed' && 'text-dark-text-muted',
          )}
        >
          {STATUS_LABELS[row.status] ?? row.status}
        </span>
      ),
    },
    {
      key: 'datetime',
      label: 'Due Date',
      sortable: true,
      filterable: true,
      filterConfig: {
        values: dueFilterValues,
        selectedValues: due,
        onChange: (selected) => setDue(selected as TodoDueHorizon[]),
      },
      width: '150px',
      render: (row) => {
        const date = new Date(row.datetime);
        // Overdue reads red, due-today amber. Only meaningful while the todo is still
        // pending — a done todo's date is history, not a deadline.
        const isDueToday = isToday(date);
        const isOverdue = isPast(endOfDay(date)) && !isDueToday;
        const urgent = row.status === 'pending';
        return (
          <div className={clsx(row.status !== 'pending' && 'opacity-60')} title={recencyLabel(date)}>
            <div
              className={clsx(
                'font-medium',
                urgent && isOverdue && 'text-red-400',
                urgent && isDueToday && 'text-amber-400',
              )}
            >
              {format(date, 'MMM d, yyyy')}
            </div>
            <div className="text-xs text-dark-text-muted">{format(date, 'h:mm a')}</div>
          </div>
        );
      },
    },
  ];

  const tableSortConfig = params.sort_by
    ? { key: params.sort_by, direction: params.sort_order ?? 'asc' }
    : null;

  const rowClassName = useCallback(
    (row: TodoItem): string | undefined => {
      const classes: string[] = [];
      if (animatingIds.has(row.id)) classes.push('todo-exit-animation');
      if (isContactOverlayOpen && row.id === selectedTodoId) {
        classes.push('ring-1 ring-inset ring-primary-500 bg-primary-900/10');
      }
      return classes.length > 0 ? classes.join(' ') : undefined;
    },
    [animatingIds, isContactOverlayOpen, selectedTodoId],
  );

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-2xl) mx-auto w-full p-3 sm:p-4 md:p-6 flex-1 flex flex-col min-h-0">
        <div className="mb-6 animate-slideDown flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-dark-text-primary mb-2 tracking-tight flex items-center gap-2">
              <Bell className="w-6 h-6" />
              Todo
            </h1>
            <p className="text-sm text-dark-text-secondary">
              {counts.overdue > 0 && (
                <span className="text-red-400 font-medium">{counts.overdue} overdue</span>
              )}
              {counts.overdue > 0 && counts.today > 0 && ' · '}
              {counts.today > 0 && (
                <span className="text-amber-400 font-medium">{counts.today} due today</span>
              )}
              {(counts.overdue > 0 || counts.today > 0) && counts.later > 0 && ' · '}
              {counts.later > 0 && <span>{counts.later} upcoming</span>}
              {counts.total === 0 && 'No todos'}
              {counts.total > 0 && (counts.by_status.pending ?? 0) === 0 && 'All done!'}
            </p>
          </div>
          <div className="flex items-center gap-2 animate-scaleIn">
            <Button
              variant="primary"
              onClick={() => setIsTodoModalOpen(true)}
              size="sm"
            >
              <Plus className="w-4 h-4" />
              New Todo
            </Button>
            <button
              onClick={handleToggleCompactMode}
              title={isCompactMode ? 'Disable compact mode' : 'Enable compact mode (single-line rows)'}
              className={clsx(
                'p-2 rounded-lg border transition-colors',
                isCompactMode
                  ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                  : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
              )}
            >
              <Rows3 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Venue error banner */}
        {venueError && (
          <div className="mb-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-between animate-fadeIn">
            <span className="text-sm text-red-400">{venueError}</span>
            <button
              onClick={() => setVenueError(null)}
              className="text-red-400 hover:text-red-300 transition-colors ml-3"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4 items-center">
          <SearchInput
            value={params.q || ''}
            onChange={setSearch}
            placeholder="Search description, venue or list..."
            className="flex-1 max-w-md"
          />
          {isFiltered && (
            <button
              onClick={resetFilters}
              className="text-xs text-dark-text-muted hover:text-dark-text-secondary transition-colors"
            >
              Reset filters
            </button>
          )}

          {/* Top pagination — the table is tall enough that the bottom controls are off-screen
              once you have scrolled into it. */}
          {pagination && pagination.totalPages > 0 && (
            <div className="sm:ml-auto">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                pageSize={pagination.pageSize}
                hasNext={pagination.hasNext}
                hasPrev={pagination.hasPrev}
                onPageChange={setPage}
                isFetching={isFetching}
                compact
              />
            </div>
          )}
        </div>

        <div className="card flex-1 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 h-full relative">
            <TableLoadingOverlay isVisible={isFetching && !isLoading} />
            {isLoading ? (
              <TableSkeleton rows={8} />
            ) : (
              <Table
                data={todoItems}
                columns={columns}
                searchable={false}
                emptyMessage={
                  todosError ? 'Failed to load todos'
                  : statuses.length === 0 ? 'No statuses selected — pick one in the Status filter.'
                  : due.length === 0 ? 'No due dates selected — pick one in the Due Date filter.'
                  : !isFiltered && counts.later > 0 ? (
                    <span>
                      Nothing due yet —{' '}
                      <button
                        onClick={() => setDue(DUE_ORDER)}
                        className="text-primary-400 hover:text-primary-300 underline underline-offset-2"
                      >
                        {counts.later} {counts.later === 1 ? 'todo is' : 'todos are'} due later. Show them.
                      </button>
                    </span>
                  )
                  : isFiltered ? (
                    <span>
                      No todos match —{' '}
                      <button
                        onClick={resetFilters}
                        className="text-primary-400 hover:text-primary-300 underline underline-offset-2"
                      >
                        reset the filters
                      </button>
                      .
                    </span>
                  )
                  : "No pending todos. You're all caught up!"
                }
                serverSort={true}
                sortConfig={tableSortConfig}
                onSort={setSort}
                rowClassName={rowClassName}
                rowContextMenu={getRowContextMenu}
                onRowModClick={handleOpenTodo}
                compact={isCompactMode}
              />
            )}
          </div>

          {pagination && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              pageSize={pagination.pageSize}
              hasNext={pagination.hasNext}
              hasPrev={pagination.hasPrev}
              onPageChange={setPage}
              isFetching={isFetching}
            />
          )}
        </div>
      </div>

      {/* Dismiss Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmAction?.type === 'dismiss'}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirmAction}
        title="Dismiss Todo?"
        message="Are you sure you want to dismiss this todo?"
        confirmLabel="Dismiss"
      />

      {/* Snooze Picker Dialog */}
      {confirmAction?.type === 'snooze' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 w-full max-w-xs mx-4">
            <h3 className="text-sm font-semibold text-dark-text-primary mb-3">Snooze Todo</h3>
            <div className="flex flex-col gap-1.5 mb-3">
              {SNOOZE_PRESETS.map((preset) => (
                <button
                  key={preset.days}
                  className="flex items-center justify-between px-3 py-2 text-sm text-dark-text-primary rounded-md hover:bg-dark-elevated transition-colors text-left"
                  onClick={() => handleSnoozePreset(preset.days)}
                >
                  <span>{preset.label}</span>
                  <span className="text-xs text-dark-text-muted">
                    {format(addDays(new Date(), preset.days), 'MMM d')}
                  </span>
                </button>
              ))}
            </div>
            <div className="border-t border-dark-border pt-3">
              <label className="text-xs text-dark-text-secondary mb-1.5 block">Custom date</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  className="input text-sm flex-1"
                  value={customSnoozeDate}
                  min={format(addDays(new Date(), 1), 'yyyy-MM-dd')}
                  max={format(addDays(new Date(), 365), 'yyyy-MM-dd')}
                  onChange={(e) => setCustomSnoozeDate(e.target.value)}
                />
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!customSnoozeDate}
                  onClick={handleSnoozeCustomDate}
                >
                  Snooze
                </Button>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Overlay */}
      <ContactOverlay
        isOpen={isContactOverlayOpen}
        onClose={() => {
          openRequest.cancel();
          setIsContactOverlayOpen(false);
          setSelectedVenue(null);
          setSelectedVenueContacts([]);
          setSelectedContactId(undefined);
          setTriggeringTodoId(null);
          setSelectedTodoId(null);
          setOverlayIndex(null);
        }}
        venue={selectedVenue}
        contacts={selectedVenueContacts}
        bands={bands}
        initialContactId={selectedContactId}
        onAddCommunication={triggeringTodoId ? async () => {
          const todoId = triggeringTodoId;
          setTriggeringTodoId(null);
          await handleMarkDone(todoId);
        } : undefined}
        onRefreshContacts={() => {
          if (selectedVenue) {
            handleOpenVenueContacts(selectedVenue._id, selectedContactId);
          }
        }}
        onRefreshVenues={() => {
          if (selectedVenue) {
            handleOpenVenueContacts(selectedVenue._id, selectedContactId);
          }
        }}
        onEditVenue={handleEditVenue}
        hasPrev={overlayHasPrev}
        hasNext={overlayHasNext}
        onNavigate={handleOverlayNavigate}
      />

      {/* Venue Edit Modal */}
      <VenueModal
        isOpen={isVenueModalOpen}
        onClose={handleCloseVenueModal}
        onSubmit={handleVenueSubmit}
        onDelete={handleDeleteVenue}
        venue={editingVenue}
        mode="edit"
        readonlyCoreFields={
          editingVenue != null && !user?.is_admin &&
          (editingVenue.visibility === 'public' || editingVenue.visibility === 'pending_public')
        }
      />

      {/* New Todo Modal */}
      <TodoModal
        isOpen={isTodoModalOpen}
        onClose={() => setIsTodoModalOpen(false)}
        onSubmit={handleCreateTodo}
        bands={bands}
      />

      {/* Edit Todo Modal */}
      <TodoModal
        isOpen={editingTodo !== null}
        onClose={() => setEditingTodo(null)}
        onSubmit={handleEditTodo}
        bands={bands}
        editMode
        initialValues={editingTodo ? {
          description: editingTodo.description,
          datetime: editingTodo.datetime,
          type: editingTodo.todoType,
          contact_id: editingTodo.contactId,
          band_ids: editingTodo.reminder.band_ids,
        } : undefined}
      />
    </div>
  );
}
